import {
  batchCreateAge,
  batchCreateBasicField,
  batchCreateSexCode,
  batchDeleteElementAndSubs,
  createFieldValue,
  getNestedOrFloatingElementById,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { FullTextApp } from '../../../../../shared/services/config'
import { compareLinkedPersons, getSharedElement } from '../../panels/linkNamesPanel/utils/linkedPersonsHelpers'
import {
  createLinkPersonDispatches,
  createUnlinkPersonDispatches,
  createUnlinkDispatch,
} from '../../panels/linkNamesPanel/utils/linkingDispatchHelpers'
import { getFieldValueText } from '../../panels/highlightPanel/utils/highlightUtils'

function handleExistingField(defaultValues, fact, existingField, attr) {
  const defaultValue = defaultValues?.additionalFacts.find((defaultVal) => defaultVal.id === fact.id)
  const newValue = getFieldValueText(fact)
  const previousValue = getFieldValueText(defaultValue)

  if (newValue !== previousValue) {
    // Handle SEX_CODE with special URI structure
    let fieldValue
    if (fact.key === 'SEX_CODE') {
      const resource = fact.value?.standard?.formalText || fact.value
      const displayText = fact.value?.standard?.standardizedText || fact?.value
      fieldValue = createFieldValue(displayText, { ...attr, reasonId: INDEXED_INCORRECTLY }, resource)
    } else {
      fieldValue = createFieldValue(newValue, { ...attr, reasonId: INDEXED_INCORRECTLY })
    }
    return [
      {
        type: 'ADD_FV',
        value: {
          id: fact.id,
          partitionKey: existingField.partitionKey,
          fieldValue,
        },
      },
    ]
  }
  return []
}

function handleNewField(person, fact, attr) {
  const existingDispatches = fact?.dispatches || []
  const existingFieldValueDispatch = existingDispatches.find((d) => d.type === 'ADD_FV' && d.value.id === fact.id)

  // If the field value was previously created (e.g by the shared highlight flow), reuse the dispatches for this fact
  // and add a dispatch to link it to the person
  if (existingFieldValueDispatch) {
    const addDispatch = {
      type: 'ADD_SUB',
      value: {
        superPartitionKey: person.partitionKey,
        subPartitionKey: person.partitionKey,
        superId: person.id,
        subId: fact.id,
        elementType: fact.key,
      },
    }

    return [...existingDispatches, addDispatch]
  }

  if (fact.key === 'SEX_CODE') {
    const resource = fact.value?.standard?.formalText || fact.value
    const displayText = fact.value?.standard?.standardizedText || fact?.value
    const standardValue = createFieldValue(displayText, { ...attr, reasonId: INDEXED_INCORRECTLY }, resource)
    return batchCreateSexCode({
      partitionKey: person.partitionKey,
      personId: person.id,
      sexFieldId: fact.id,
      sexFieldValue: standardValue,
    })
  }

  const fieldValue = createFieldValue(fact.value, { ...attr, reasonId: INDEXED_INCORRECTLY })

  if (fact.key === 'AGE') {
    return batchCreateAge({
      partitionKey: person.partitionKey,
      personId: person.id,
      id: fact.id,
      fieldType: fact.key,
      fieldValue,
    })
  }

  return batchCreateBasicField({
    partitionKey: person.partitionKey,
    superId: person.id,
    id: fact.id,
    fieldType: fact.key,
    superElementType: 'PERSON',
    fieldValue: createFieldValue(fact.value, { ...attr, reasonId: INDEXED_INCORRECTLY }),
  })
}

/**
 * Generates dispatch actions for editing, adding, and removing additional facts.
 * @param {Array} additionalFacts - the values of the form associated with additional facts
 * @param {Object} defaultValues - values the form was initialized with
 * @param {Object} person - the SLS person object being edited
 * @param {Array} images - the SLS images array
 * @returns {Array} Array of dispatch actions to be processed
 */
export default function getAdditionalFactDispatches({ additionalFacts, defaultValues, person, images }) {
  let dispatches = []
  const attr = { comment: '', reasonId: '', app: FullTextApp } // Eric said to use an empty reason id when creating new fields

  additionalFacts.forEach((fact) => {
    if (fact.key === 'SEX_CODE' && !fact.value?.standard) {
      const existingField = getNestedOrFloatingElementById(images, fact.id)
      if (!existingField) return
      if (existingField.superElements?.length > 1) {
        dispatches.push(
          createUnlinkDispatch({
            person,
            element: existingField,
          })
        )
      } else {
        dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: existingField }))
      }
      return
    }

    const existingField = getNestedOrFloatingElementById(images, fact.id)
    if (existingField) {
      dispatches = dispatches.concat(handleExistingField(defaultValues, fact, existingField, attr))
    } else {
      dispatches = dispatches.concat(handleNewField(person, fact, attr))
    }

    // Handle links/unlinks
    const orig = defaultValues.additionalFacts.find((f) => f.id === fact.id) || {}
    const { addedPersons, removedPersons } = compareLinkedPersons(
      orig.linkedPersonIds || [],
      fact.linkedPersonIds || []
    )

    addedPersons.forEach((p) => {
      const addedPerson = getNestedOrFloatingElementById(images, p.id)
      const sharedElement = getSharedElement(images, fact.id)

      const linkDispatches = createLinkPersonDispatches({
        person: addedPerson,
        sharedElement,
        images,
      })

      dispatches.push(...linkDispatches)
    })

    removedPersons.forEach((p) => {
      const removedPerson = getNestedOrFloatingElementById(images, p.id)
      const factElement = getNestedOrFloatingElementById(images, fact.id)

      const unlinkDispatches = createUnlinkPersonDispatches({
        person: removedPerson,
        fieldId: factElement.id,
        fieldKey: fact.key,
        partitionKey: factElement.partitionKey,
        images,
      })

      dispatches.push(...unlinkDispatches)
    })
  })

  defaultValues.additionalFacts.forEach((fact) => {
    if (!additionalFacts.find((af) => af.id === fact.id)) {
      const existingField = getNestedOrFloatingElementById(images, fact.id)
      if (existingField) {
        if (existingField.superElements?.length > 1) {
          // Field is shared, so just unlink it from this person
          dispatches.push(
            createUnlinkDispatch({
              person,
              element: existingField,
            })
          )
        } else {
          let elementToDelete = existingField

          // Handle unlinking for AGE fields stored under an intermediate element
          if (existingField.fieldType === 'AGE') {
            const parentElementId = existingField.superElements?.[0]?.id
            const ageElement = getNestedOrFloatingElementById(images, parentElementId)
            if (ageElement && ageElement?.elementType === 'AGE') {
              elementToDelete = ageElement
            }
          }

          // Field was deleted in the form, so delete it in SLS
          dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: elementToDelete }))
        }
      }
    }
  })

  return dispatches
}
